import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Lock } from 'lucide-react'
import {
  OnboardingUserType,
  type OnboardingStateResponse,
} from '@super/shared-types'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAppAuth } from '@/contexts/app-auth.context'
import type { OnboardingStep } from './flow.config'
import { previousStep, progressStepsFor } from './flow.config'
import { OnboardingProgressBar } from './OnboardingProgressBar'

interface OnboardingHeaderProps {
  currentStep: OnboardingStep
  state: OnboardingStateResponse | null
  onTogglePath: (next: OnboardingUserType) => void
  /**
   * Optimistic userType — set by the layout the moment the user clicks the
   * toggle so the label + counter update without waiting for the PATCH
   * response. Falls back to `state.userType`.
   */
  displayUserType?: OnboardingUserType | null
  /** Once a card is on file, the path can no longer be switched. */
  pathLocked?: boolean
}

const PATH_LABEL: Record<OnboardingUserType, string> = {
  [OnboardingUserType.SDK_BUILDER]: 'SDK Builder',
  [OnboardingUserType.STANDARD_USER]: 'Standard',
}

function otherPath(current: OnboardingUserType | null): OnboardingUserType {
  return current === OnboardingUserType.SDK_BUILDER
    ? OnboardingUserType.STANDARD_USER
    : OnboardingUserType.SDK_BUILDER
}

export function OnboardingHeader({
  currentStep,
  state,
  onTogglePath,
  displayUserType,
  pathLocked = false,
}: OnboardingHeaderProps) {
  const navigate = useNavigate()
  const { signOut } = useAppAuth()
  const userType = displayUserType ?? state?.userType ?? null
  const progressSteps = progressStepsFor(userType)
  const currentIndex = progressSteps.findIndex(s => s.id === currentStep.id)
  const isComplete = state?.status === 'completed'
  const counterLabel = isComplete
    ? `${progressSteps.length} / ${progressSteps.length} · COMPLETE`
    : currentIndex >= 0
      ? `${currentIndex + 1} / ${progressSteps.length}`
      : null

  const previous = previousStep(currentStep, userType)
  const showBack = previous !== null

  const handleBack = () => {
    if (previous) navigate(previous.route)
  }

  return (
    <header className="border-border/60 bg-background/95 border-b backdrop-blur-lg backdrop-saturate-150">
      <div className="mx-auto flex h-16 w-full max-w-[840px] items-center gap-3 px-4 text-[11px] font-medium tracking-[0.12em]">
        {showBack ? (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="text-muted-foreground hover:text-foreground -ml-2 flex h-11 w-11 items-center justify-center transition"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : (
          <span className="-ml-2 h-11 w-11" aria-hidden="true" />
        )}
        <span className="text-foreground font-fragment-mono uppercase">
          {currentStep.label}
        </span>
        <span className="flex-1" />
        {userType && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground flex h-11 items-center gap-1.5 px-2 text-sm font-normal tracking-normal transition"
              >
                {PATH_LABEL[userType]}
                {pathLocked ? (
                  <Lock className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {pathLocked ? (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        aria-disabled="true"
                        className="text-muted-foreground/60 flex cursor-not-allowed items-center gap-2 px-2 py-1.5 text-sm"
                      >
                        <Lock className="h-3 w-3" />
                        Switch path locked
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-xs">
                      Please contact support to change user paths.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <DropdownMenuItem
                  onClick={() => onTogglePath(otherPath(userType))}
                >
                  Switch to {PATH_LABEL[otherPath(userType)]}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut}>Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {counterLabel && (
          <span className="text-muted-foreground font-fragment-mono uppercase">
            {counterLabel}
          </span>
        )}
      </div>
      <OnboardingProgressBar currentStep={currentStep} userType={userType} />
    </header>
  )
}
